import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import FormLabel from '@mui/material/FormLabel';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router';
import { organizationApi } from 'src/api/organizationApi';
import { Button } from 'src/common';
import { useUserProfile } from 'src/contexts/UserProfileContext';
import { SubscriptionCreateForm } from './SubscriptionCreateForm';
import { SubscriptionFieldPreview } from './SubscriptionFieldPreview';
import type { SubscriptionCreateDraft } from './types';

type ClientOption = { id: string; label: string; value: string };

const mapClientOptions = (clients: any[]): ClientOption[] =>
  (Array.isArray(clients) ? clients : [])
    .map((client, index) => {
      const id = String(client?.id || client?._id || '');
      const label = String(client?.name || client?.organizationName || '').trim();
      if (!id || !label) return null;
      return { id: id || `client-${index}`, label, value: id };
    })
    .filter(Boolean) as ClientOption[];

export const QuickCreateSubscription = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { userData } = useUserProfile();
  const [clientOptions, setClientOptions] = useState<ClientOption[]>([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState<ClientOption | null>(null);
  const [draft, setDraft] = useState<SubscriptionCreateDraft | null>(null);

  useEffect(() => {
    let cancelled = false;
    const orgId = userData?.organization?.id;
    if (!orgId) {
      setClientOptions([]);
      return;
    }

    setClientsLoading(true);
    organizationApi
      .getByParentId(orgId, 0, 100)
      .then((response) => {
        if (cancelled) return;
        const clients = Array.isArray(response?.data?.data)
          ? response.data.data
          : Array.isArray(response?.data)
            ? response.data
            : [];
        setClientOptions(mapClientOptions(clients));
      })
      .catch(() => {
        if (cancelled) return;
        setClientOptions([]);
        toast.error('Failed to load clients');
      })
      .finally(() => {
        if (!cancelled) setClientsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [userData?.organization?.id]);

  const handleCancel = () => {
    navigate('/subscriptions');
  };

  if (draft) {
    return (
      <Paper
        elevation={0}
        style={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 8,
          padding: 24,
          backgroundColor: '#fff',
        }}>
        <SubscriptionFieldPreview
          draft={draft}
          onBack={() => setDraft(null)}
          onSaved={handleCancel}
        />
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      style={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        padding: 24,
        backgroundColor: '#fff',
      }}>
      <Typography
        style={{
          fontSize: 18,
          fontWeight: 700,
          marginBottom: 20,
          color: theme.palette.text.primary,
        }}>
        Create Subscription
      </Typography>

      <Box style={{ maxWidth: 420, marginBottom: 20 }}>
        <FormLabel
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: theme.palette.text.secondary,
            marginBottom: 6,
            display: 'block',
          }}>
          Client
        </FormLabel>
        <Autocomplete
          options={clientOptions}
          loading={clientsLoading}
          value={selectedClient}
          onChange={(_event, value) => {
            setSelectedClient(value);
            setDraft(null);
          }}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          renderOption={(props, option) => (
            <li {...props} key={option.id} style={{ fontSize: 12 }}>
              {option.label}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Select client"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {clientsLoading ? <CircularProgress color="inherit" size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: 50,
                  fontSize: 12,
                },
                '& .MuiInputBase-input::placeholder': {
                  fontSize: 12,
                },
              }}
            />
          )}
          ListboxProps={{
            style: { fontSize: 12, maxHeight: 320 },
          }}
        />
      </Box>

      {selectedClient ? (
        <SubscriptionCreateForm
          key={selectedClient.value}
          clientId={selectedClient.value}
          clientName={selectedClient.label}
          onNext={setDraft}
          onCancel={handleCancel}
        />
      ) : (
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Button
            type="button"
            variant="outlined"
            color="inherit"
            onClick={handleCancel}
            sx={{ minWidth: 120, height: 42 }}>
            Cancel
          </Button>
          <Typography style={{ fontSize: 13, color: theme.palette.text.secondary }}>
            Select a client to add products and packages
          </Typography>
        </Box>
      )}
    </Paper>
  );
};
